import {
    formatRelativeDay as formatChatDayLabel,
    getLocalDayKey as toLocalDayKey,
} from '@/shared/lib/dateTime';

import type {
    ChatFilter,
    ChatMessage,
    ChatMessageGroup,
    ChatSummary,
    DisplayChatMessage,
    PendingChatMessage,
} from './types';

export {
    formatRelativeDay as formatChatDayLabel,
    formatRelativeTimestamp as formatChatListTimestamp,
    formatTime24 as formatChatMessageTime,
} from '@/shared/lib/dateTime';

/** Возвращает копию списка чатов от новых сообщений к старым. */
export const sortChatsByLastMessage = (chats: readonly ChatSummary[]): ChatSummary[] => (
    chats
        .map((chat, index) => ({
            chat,
            index
        }))
        .sort((left, right) => {
            const leftTime = left.chat.lastMessageAt ?? Number.NEGATIVE_INFINITY;
            const rightTime = right.chat.lastMessageAt ?? Number.NEGATIVE_INFINITY;

            return (rightTime - leftTime) || (left.index - right.index);
        })
        .map(({ chat }) => chat)
);

/** Выбирает непустые чаты для активного фильтра в порядке актуальности. */
export const selectVisibleChats = (
    chats: readonly ChatSummary[],
    filter: ChatFilter,
): ChatSummary[] => {
    const visibleChats = chats.filter((chat) => (
        chat.lastMessage.trim().length > 0
        && (filter === 'all' || chat.companion.role === filter)
    ));

    return sortChatsByLastMessage(visibleChats);
};

/** Форматирует количество непрочитанных сообщений для компактного бейджа. */
export const formatUnreadCount = (count: number): string | null => {
    if (!Number.isFinite(count) || count <= 0) return null;

    const normalizedCount = Math.floor(count);

    return normalizedCount > 99
        ? '99+'
        : normalizedCount.toString();
};

/** Возвращает копию сообщений в хронологическом и стабильном порядке. */
export const sortChatMessagesByCreatedAt = <TMessage extends ChatMessage>(
    messages: readonly TMessage[],
): TMessage[] => (
    messages
        .map((message, index) => ({
            message,
            index
        }))
        .sort((left, right) => (
            (left.message.createdAt - right.message.createdAt) || (left.index - right.index)
        ))
        .map(({ message }) => message)
);

/** Сравнивает наборы фотографий сообщений */
const haveSamePhotoUrls = (
    left: readonly string[],
    right: readonly string[],
): boolean => (
    left.length === right.length
    && left.every((url, index) => url === right[index])
);

/** Определяет подтверждение локального сообщения */
const isPendingMessageConfirmed = (
    message: ChatMessage,
    pendingMessage: PendingChatMessage,
): boolean => (
    message.id === pendingMessage.id
    || (
        message.isMine
        && !pendingMessage.knownMessageIds.includes(message.id)
        && message.message === pendingMessage.message
        && haveSamePhotoUrls(message.photoUrls, pendingMessage.photoUrls)
    )
);

/** Оставляет optimistic-сообщения, для которых ещё нет подтверждения Firestore. */
export const selectUnconfirmedPendingMessages = (
    messages: readonly ChatMessage[],
    pendingMessages: readonly PendingChatMessage[],
): PendingChatMessage[] => {
    const availableMessages = [...messages];

    return pendingMessages.filter((pendingMessage) => {
        const confirmedIndex = availableMessages.findIndex((message) => (
            isPendingMessageConfirmed(message, pendingMessage)
        ));

        if (confirmedIndex < 0) return true;

        availableMessages.splice(confirmedIndex, 1);

        return false;
    });
};

/** Объединяет подтверждённые и локальные сообщения без повторения подтверждённых ID. */
export const mergeChatMessages = (
    messages: readonly ChatMessage[],
    pendingMessages: readonly PendingChatMessage[],
): DisplayChatMessage[] => {
    const knownIds = new Set(messages.map((message) => message.id));
    const unconfirmedMessages = selectUnconfirmedPendingMessages(messages, pendingMessages);

    const uniquePendingMessages = unconfirmedMessages.filter((message) => {
        if (knownIds.has(message.id)) return false;
        knownIds.add(message.id);

        return true;
    });

    return sortChatMessagesByCreatedAt<DisplayChatMessage>([
        ...messages,
        ...uniquePendingMessages,
    ]);
};

/** Группирует сообщения по локальным календарным дням в хронологическом порядке. */
export const groupChatMessagesByDay = <TMessage extends ChatMessage>(
    messages: readonly TMessage[],
    now = new Date(),
    locale = 'ru-RU',
): ChatMessageGroup<TMessage>[] => {
    const groups: ChatMessageGroup<TMessage>[] = [];

    for (const message of sortChatMessagesByCreatedAt(messages)) {
        const key = toLocalDayKey(message.createdAt);
        const currentGroup = groups[groups.length - 1];

        if (currentGroup?.key === key) {
            currentGroup.messages.push(message);
            continue;
        }

        groups.push({
            key,
            label: formatChatDayLabel(message.createdAt, now, locale),
            messages: [message],
        });
    }

    return groups;
};
